import React from 'react';
import { useSearchParams } from 'react-router-dom';
import AdminPageShell from '../components/AdminPageShell';
import AdminTabs from '../components/AdminTabs';
import SessionsView from './legislature/SessionsView';
import CyclesView from './legislature/CyclesView';
import MilestoneTypesView from './elections/MilestoneTypesView';
import { HelpTrigger } from '../../help';

const TABS = [
  { key: 'sessions', label: 'Sessions' },
  { key: 'cycles', label: 'Cycles' },
  { key: 'milestone-types', label: 'Milestone Types' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

/**
 * Legislature — registries describing legislatures themselves, as opposed to
 * Elections, which describes the electoral cycle around them.
 *
 * It is a separate page rather than more Elections tabs because a legislative
 * session is not an election cycle: they are different periods with different
 * boundaries, and folding them together is how the app ended up with six competing
 * ideas of "current" in the first place (LEGISLATIVE_SESSION_MODEL_PRD.md §5.R11).
 *
 * Cycles group sessions into terms of a body — each General Assembly, each
 * Congress — and are where officeholder term dates come from; their milestone
 * types share the Elections tab's code (ELECTION_OUTCOMES_PRD.md §5.0).
 */
const LegislaturePage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab') as TabKey | null;
  const activeTab: TabKey = TABS.some((tab) => tab.key === requestedTab)
    ? (requestedTab as TabKey)
    : 'sessions';

  return (
    <AdminPageShell
      title="Legislature"
      description="Legislative sessions and cycles — what is current across the app, and when terms start and end"
      showHeading={false}
    >
      <div className="flex items-center gap-2">
        {/*
          Three short tabs always stay across the bar (overflow="none"), never in a
          "More" menu. flex-1 lets the bar take the row beside the help trigger.
        */}
        <div className="min-w-0 flex-1">
          <AdminTabs
            tabs={[...TABS]}
            activeKey={activeTab}
            overflow="none"
            onChange={(key) => setSearchParams({ tab: key }, { replace: true })}
          />
        </div>
        {activeTab === 'sessions'
          ? <HelpTrigger contentKey="admin-sessions" />
          : <HelpTrigger contentKey="legislative-cycles" />}
      </div>

      {activeTab === 'sessions' && <SessionsView />}
      {activeTab === 'cycles' && <CyclesView />}
      {activeTab === 'milestone-types' && <MilestoneTypesView domain="legislative" />}
    </AdminPageShell>
  );
};

export default LegislaturePage;
